import { z } from 'zod';
/**
 * Validierung des Regelbaums.
 *
 * Die Struktur ist rekursiv, deshalb z.lazy. Die Tiefe begrenzt zusaetzlich
 * der Builder in @mailflow/core — hier geht es um die Form, dort um die
 * Belastbarkeit.
 */
const timeUnit = z.enum(['hours', 'days', 'weeks', 'months']);
export const timeframe = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('last'), unit: timeUnit, value: z.number().positive().max(3650) }),
    z.object({
        kind: z.literal('older_than'),
        unit: timeUnit,
        value: z.number().positive().max(3650),
    }),
    z.object({ kind: z.literal('before'), date: z.string() }),
    z.object({ kind: z.literal('after'), date: z.string() }),
    z.object({ kind: z.literal('between'), from: z.string(), to: z.string() }),
]);
export const countFilter = z.object({
    op: z.enum(['=', '>', '>=', '<', '<=', 'between']),
    value: z.number().int().min(0),
    value2: z.number().int().min(0).optional(),
});
const attributeOperator = z.enum([
    'equals',
    'not_equals',
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    'in',
    'not_in',
    'is_empty',
    'is_not_empty',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
]);
const emailEventCondition = z.object({
    type: z.literal('email_event'),
    event: z.enum([
        'sent',
        'delivered',
        'open',
        'click',
        'bounce_hard',
        'bounce_soft',
        'complaint',
        'unsubscribe',
        'reply',
        'failed',
    ]),
    negate: z.boolean().optional(),
    scope: z
        .object({
        campaignId: z.string().uuid().optional(),
        journeyId: z.string().uuid().optional(),
        journeyNodeId: z.string().optional(),
    })
        .optional(),
    timeframe: timeframe.optional(),
    count: countFilter.optional(),
    uniqueOnly: z.boolean().optional(),
    excludeMachine: z.boolean().optional(),
    urlContains: z.string().max(500).optional(),
});
const attributeCondition = z.object({
    type: z.literal('attribute'),
    field: z.enum([
        'email',
        'firstName',
        'lastName',
        'phone',
        'status',
        'source',
        'country',
        'city',
        'leadScore',
        'lifecycleStage',
        'consentStatus',
        'createdAt',
        'lastActivityAt',
    ]),
    op: attributeOperator,
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    value2: z.union([z.string(), z.number()]).optional(),
    values: z
        .array(z.union([z.string(), z.number()]))
        .max(500)
        .optional(),
    negate: z.boolean().optional(),
});
const customFieldCondition = z.object({
    type: z.literal('custom_field'),
    key: z.string().min(1).max(120),
    op: attributeOperator,
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    values: z
        .array(z.union([z.string(), z.number()]))
        .max(500)
        .optional(),
    negate: z.boolean().optional(),
});
const customEventCondition = z.object({
    type: z.literal('custom_event'),
    name: z.string().min(1).max(120),
    negate: z.boolean().optional(),
    timeframe: timeframe.optional(),
    count: countFilter.optional(),
    aggregate: z
        .object({
        fn: z.enum(['sum', 'avg', 'max', 'min']),
        property: z.string().max(120).optional(),
        op: z.enum(['=', '>', '>=', '<', '<=']),
        value: z.number(),
    })
        .optional(),
});
const membershipCondition = z.union([
    z.object({
        type: z.literal('list_member'),
        listId: z.string().uuid(),
        negate: z.boolean().optional(),
    }),
    z.object({ type: z.literal('tag'), tagId: z.string().uuid(), negate: z.boolean().optional() }),
    z.object({
        type: z.literal('in_segment'),
        segmentId: z.string().uuid(),
        negate: z.boolean().optional(),
    }),
]);
export const segmentCondition = z.union([
    emailEventCondition,
    attributeCondition,
    customFieldCondition,
    customEventCondition,
    membershipCondition,
]);
export const segmentNode = z.lazy(() => z.union([
    segmentCondition,
    z.object({
        op: z.enum(['AND', 'OR']),
        children: z.array(segmentNode).max(50),
    }),
]));
export const createSegmentRequest = z.object({
    name: z.string().trim().min(1, 'Name fehlt').max(120),
    description: z.string().max(500).optional().nullable(),
    definition: segmentNode,
    mode: z.enum(['live', 'materialized']).default('live'),
});
export const updateSegmentRequest = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().max(500).optional().nullable(),
    definition: segmentNode.optional(),
});
/** Auswertung ohne Speichern — traegt die Live-Vorschau im Builder. */
export const previewSegmentRequest = z.object({
    definition: segmentNode,
    sampleSize: z.number().int().min(0).max(50).default(20),
});
export const segmentEvaluation = z.object({
    total: z.number().int(),
    totalContacts: z.number().int(),
    sample: z.array(z.object({
        id: z.string().uuid(),
        email: z.string(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        status: z.string(),
    })),
    durationMs: z.number().int(),
});
//# sourceMappingURL=segments.js.map